/**
 * Maestros · Clientes — filtro del maestro de clientes contra ACSIS.
 *
 * Dos criterios combinados con AND (ID + nombre) y un solo botón Buscar: el
 * back filtra sobre el maestro completo, porque los endpoints de listado de
 * ACSIS ignoran cualquier parámetro de filtro. El campo nombre también matchea
 * nombre legal y CUIT.
 *
 * De la fila se pasa a la ficha completa (`/api/clientes/:id`), que trae la
 * tabla de domicilios con el GLN destino de cada uno.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2, ArrowLeft, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { buscarClientePorId, listarClientes } from '@/api/endpoints/cliente-acsis';
import type {
  ClienteAcsis,
  ClienteDomicilio,
  ClienteListItem,
} from '@/api/contracts/cliente-acsis';

export function ClientesTab() {
  const [id, setId] = useState('');
  const [nombre, setNombre] = useState('');
  /** Filtro efectivamente aplicado (se congela al apretar Buscar). */
  const [filtro, setFiltro] = useState<{ id: string; nombre: string } | null>(null);
  const [seleccionado, setSeleccionado] = useState<string | null>(null);

  const listado = useQuery({
    queryKey: ['clientes-listar', filtro?.id ?? '', filtro?.nombre ?? ''],
    queryFn: () => listarClientes({ id: filtro?.id, nombre: filtro?.nombre }),
    enabled: filtro !== null,
    retry: false,
  });

  const detalle = useQuery({
    queryKey: ['cliente-acsis', seleccionado],
    queryFn: () => buscarClientePorId(seleccionado ?? ''),
    enabled: Boolean(seleccionado),
    retry: false,
  });

  // Atajo del flujo anterior (escribir el ID exacto y ver la ficha): si el
  // filtro deja una sola coincidencia, la abrimos sin obligar al click.
  const items = listado.data?.items;
  useEffect(() => {
    const unico = items?.length === 1 ? items[0] : null;
    if (unico?.id) setSeleccionado(unico.id);
  }, [items]);

  const onBuscar = (e: React.FormEvent) => {
    e.preventDefault();
    setSeleccionado(null);
    setFiltro({ id: id.trim(), nombre: nombre.trim() });
  };

  const onLimpiar = () => {
    setId('');
    setNombre('');
    setFiltro(null);
    setSeleccionado(null);
  };

  const hayTexto = id.trim() !== '' || nombre.trim() !== '';

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4">
          <form onSubmit={onBuscar}>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="cliente-id" className="text-xs text-gray-600">
                  ID de cliente
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="cliente-id"
                    value={id}
                    onChange={(e) => setId(e.target.value)}
                    placeholder="Ej. 110"
                    className="pl-9 font-mono"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cliente-nombre" className="text-xs text-gray-600">
                  Nombre / razón social / CUIT
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="cliente-nombre"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    placeholder="Ej. DROGUERIA"
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
              <p className="text-[11px] text-gray-500">
                Los dos campos se combinan: se listan los clientes que cumplen ambos.
              </p>
              {/* Acciones a la derecha: Limpiar como secundaria, Buscar al borde. */}
              <div className="ml-auto flex items-center gap-2">
                {(hayTexto || filtro) && (
                  <Button type="button" variant="ghost" onClick={onLimpiar} className="text-gray-600">
                    <X className="mr-1 h-4 w-4" />
                    Limpiar
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={listado.isFetching}
                  className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
                >
                  {listado.isFetching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  <span className="ml-1.5">Buscar</span>
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {listado.error ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">
            {listado.error.message}
          </AlertDescription>
        </Alert>
      ) : listado.isLoading ? (
        <EstadoCargando texto="Consultando maestro del ERP..." />
      ) : !listado.data ? (
        <Card>
          <CardContent className="py-8 text-center text-xs text-gray-500">
            Ingresá un ID, un nombre (o los dos) y presioná <b>Buscar</b> para
            filtrar el maestro de clientes del ERP.
          </CardContent>
        </Card>
      ) : seleccionado ? (
        <FichaSeleccionada
          onVolver={() => setSeleccionado(null)}
          cantidad={listado.data.total}
          detalle={detalle}
        />
      ) : (
        <ResultadosClientes
          items={listado.data.items}
          total={listado.data.total}
          truncado={listado.data.truncado}
          totalMaestro={listado.data.totalMaestro}
          onSeleccionar={setSeleccionado}
        />
      )}
    </div>
  );
}

function EstadoCargando({ texto }: { texto: string }) {
  return (
    <div className="flex items-center justify-center py-12 text-gray-600">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
      <span>{texto}</span>
    </div>
  );
}

/** Ficha del cliente elegido + vuelta a la tabla de resultados. */
function FichaSeleccionada({
  onVolver,
  cantidad,
  detalle,
}: {
  onVolver: () => void;
  cantidad: number;
  detalle: {
    data?: { cliente: ClienteAcsis };
    isLoading: boolean;
    error: Error | null;
  };
}) {
  return (
    <div className="space-y-3">
      <Button
        type="button"
        variant="ghost"
        onClick={onVolver}
        className="h-8 px-2 text-xs text-everex-azul hover:bg-everex-azul-bg"
      >
        <ArrowLeft className="mr-1 h-3.5 w-3.5" />
        Volver a los resultados ({cantidad})
      </Button>
      {detalle.error ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">{detalle.error.message}</AlertDescription>
        </Alert>
      ) : detalle.isLoading || !detalle.data ? (
        <EstadoCargando texto="Consultando ERP..." />
      ) : (
        <ClienteCard cliente={detalle.data.cliente} />
      )}
    </div>
  );
}

/** Tabla de coincidencias. Cada fila abre la ficha del cliente. */
function ResultadosClientes({
  items,
  total,
  truncado,
  totalMaestro,
  onSeleccionar,
}: {
  items: ClienteListItem[];
  total: number;
  truncado: boolean;
  totalMaestro: number;
  onSeleccionar: (id: string) => void;
}) {
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-xs text-gray-500">
          Ningún cliente del maestro ({totalMaestro}) coincide con el filtro.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-xs text-gray-600">
            <b className="text-everex-azul">{total}</b>{' '}
            {total === 1 ? 'coincidencia' : 'coincidencias'} sobre {totalMaestro} clientes
            {truncado && <> · mostrando los primeros {items.length}</>}
          </span>
          <span className="text-[11px] text-gray-500">Click en una fila para ver la ficha</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">ID</th>
                <th className="px-3 py-2 text-left">Nombre</th>
                <th className="px-3 py-2 text-left">CUIT</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-left">Localidad</th>
                <th
                  className="px-3 py-2 text-left"
                  title="GLN del domicilio principal — guardado en el campo `barrio` del ERP"
                >
                  GLN principal
                </th>
                <th className="px-3 py-2 text-center">Dom.</th>
              </tr>
            </thead>
            <tbody className="text-gray-800">
              {items.map((c) => (
                <tr
                  key={c.id ?? c.nombre}
                  onClick={() => c.id && onSeleccionar(c.id)}
                  className="cursor-pointer border-t border-gray-100 transition hover:bg-everex-azul-bg"
                >
                  <td className="px-3 py-2 font-mono font-semibold text-everex-azul">
                    {c.id ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    {c.nombre ?? '—'}
                    {c.nombreLegal && c.nombreLegal !== c.nombre && (
                      <span className="block text-[10px] text-gray-500">{c.nombreLegal}</span>
                    )}
                    {!c.activo && (
                      <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                        dado de baja
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono">{c.cuit ?? '—'}</td>
                  <td className="px-3 py-2">{c.tipoCliente ?? '—'}</td>
                  <td className="px-3 py-2">
                    {c.localidad ?? '—'}
                    {c.provincia && (
                      <span className="text-gray-500"> ({c.provincia})</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {c.gln ?? <span className="italic text-gray-400">sin configurar</span>}
                  </td>
                  <td className="px-3 py-2 text-center">{c.cantidadDomicilios}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function ClienteCard({ cliente }: { cliente: ClienteAcsis }) {
  return (
    <Card>
      <CardContent className="pt-4">
        {/* Header */}
        <div className="mb-4 flex flex-wrap items-baseline gap-3 border-b border-gray-100 pb-3">
          <span className="rounded bg-everex-azul/10 px-2 py-1 font-mono text-sm font-bold text-everex-azul">
            #{cliente.id ?? '—'}
          </span>
          <span className="text-base font-semibold text-gray-800">
            {cliente.nombre ?? '—'}
          </span>
          {cliente.tipoCliente && (
            <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-gray-600">
              {cliente.tipoCliente}
            </span>
          )}
        </div>

        {/* Datos principales */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs md:grid-cols-3">
          <Dato label="Nombre legal">{cliente.nombreLegal ?? '—'}</Dato>
          <Dato label="CUIT" mono>
            {cliente.cuit ?? '—'}
          </Dato>
          <Dato label="Condición IVA">{cliente.condicionIVA ?? '—'}</Dato>
          {cliente.ingresosBrutos && (
            <Dato label="Ingresos brutos" mono>
              {cliente.ingresosBrutos}
            </Dato>
          )}
          <Dato label="Actividad">{cliente.actividad ?? '—'}</Dato>
          <Dato label="Vendedor">{cliente.vendedor ?? '—'}</Dato>
          <Dato label="Zona venta">{cliente.zonaVenta ?? '—'}</Dato>
          <Dato label="Cobrador">{cliente.cobrador ?? '—'}</Dato>
          <Dato label="Transporte">{cliente.transporte ?? '—'}</Dato>
          <Dato label="Cond. pago">{cliente.condicionPago ?? '—'}</Dato>
          <Dato label="Moneda CC">{cliente.monedaCuentaCorriente ?? '—'}</Dato>
          <Dato label="Cuenta contable" mono>
            {cliente.cuentaContable ?? '—'}
          </Dato>
          <Dato label="Fecha alta">{cliente.fechaAlta?.slice(0, 10) ?? '—'}</Dato>
          {cliente.fechaBaja && (
            <Dato label="Fecha baja">{cliente.fechaBaja.slice(0, 10)}</Dato>
          )}
          {cliente.codigoProveedor && (
            <Dato label="Cód. proveedor" mono>
              {cliente.codigoProveedor}
            </Dato>
          )}
        </div>

        {/* Flags */}
        <Seccion titulo="Configuración">
          <FlagBadges flags={cliente.flags} />
        </Seccion>

        {/* Clasificadores */}
        <Seccion titulo="Clasificadores">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs md:grid-cols-5">
            {(Object.keys(cliente.clasificadores) as Array<keyof typeof cliente.clasificadores>).map(
              (k) => (
                <Dato key={k} label={k.toUpperCase()} mono>
                  {cliente.clasificadores[k] ?? '—'}
                </Dato>
              ),
            )}
          </div>
        </Seccion>

        {/* Domicilios */}
        <Seccion titulo={`Domicilios (${cliente.domicilios.length})`}>
          {cliente.domicilios.length === 0 ? (
            <p className="text-xs text-gray-500">Sin domicilios cargados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Domicilio</th>
                    <th className="px-3 py-2 text-left">CP</th>
                    <th className="px-3 py-2 text-left">Provincia</th>
                    <th
                      className="px-3 py-2 text-left"
                      title="GLN destino — guardado en el campo `barrio` del ERP"
                    >
                      GLN
                    </th>
                    <th className="px-3 py-2 text-left">Teléfono</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-center">Princ.</th>
                  </tr>
                </thead>
                <tbody className="text-gray-800">
                  {cliente.domicilios.map((d) => (
                    <DomicilioRow key={d.renglon ?? Math.random()} dom={d} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Seccion>
      </CardContent>
    </Card>
  );
}

function DomicilioRow({ dom }: { dom: ClienteDomicilio }) {
  return (
    <tr className="border-t border-gray-100">
      <td className="px-3 py-2 font-mono text-gray-500">{dom.renglon ?? '—'}</td>
      <td className="px-3 py-2">{dom.domicilio ?? '—'}</td>
      <td className="px-3 py-2 font-mono">{dom.codigoPostal ?? '—'}</td>
      <td className="px-3 py-2">{dom.provincia ?? '—'}</td>
      <td className="px-3 py-2 font-mono">
        {dom.gln ?? <span className="text-gray-400 italic">sin configurar</span>}
      </td>
      <td className="px-3 py-2">{dom.telefono ?? '—'}</td>
      <td className="px-3 py-2 text-[11px]">{dom.email ?? '—'}</td>
      <td className="px-3 py-2 text-center">
        {dom.esPrincipal ? (
          <span className="rounded bg-everex-azul/10 px-1.5 py-0.5 text-[10px] font-semibold text-everex-azul">
            ✓
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
    </tr>
  );
}

function FlagBadges({ flags }: { flags: ClienteAcsis['flags'] }) {
  const items = (Object.keys(flags) as Array<keyof typeof flags>).map((k) => ({
    key: k,
    label: flagLabel(k),
    on: flags[k],
  }));
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => (
        <span
          key={it.key}
          className={cn(
            'rounded px-2 py-0.5 text-[10px] font-medium',
            it.on
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-500 line-through',
          )}
          title={it.key}
        >
          {it.label}
        </span>
      ))}
    </div>
  );
}

function flagLabel(key: keyof ClienteAcsis['flags']): string {
  switch (key) {
    case 'esGlobal': return 'Global';
    case 'bloqueadoPedidos': return 'Bloqueado pedidos';
    case 'bloqueadoFacturas': return 'Bloqueado facturas';
    case 'habilitadoWeb': return 'Habilitado web';
    case 'habilitaConciliacion': return 'Habilita conciliación';
  }
}

function Seccion({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {titulo}
      </div>
      {children}
    </div>
  );
}

function Dato({
  label,
  mono = false,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className={cn('mt-0.5 text-gray-800', mono && 'font-mono')}>{children}</div>
    </div>
  );
}
